import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from "react-redux";
import {compose} from "recompose";
import {convertStartEndDatesForFile} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from "../shared/tableFunctions";
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseSystemsResourceMap';
import '../shared/react-table.css';
import {reportStyles} from '../shared/styles-reports';
import FlatButton from "material-ui/FlatButton";
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import TableIcon from 'material-ui/svg-icons/action/receipt';

/**
 * Class for retrieving and displaying Systems Summary in a single table
 *
 */
class SystemsReport extends Component {
    constructor(props) {
        super(props);
        this.state = {
            summaryList: [],
            summaryTableData: [],
            summaryTableHeaders: [
                {label: 'System Name', key: 'system_name'},
                {label: 'Location Limit', key: 'location_limit'},
                {label: 'Location Used', key: 'location_used'},
                {label: 'Location Overage', key: 'location_overage'},
                {label: 'Device Limit', key: 'device_limit'},
                {label: 'Device Used', key: 'device_used'},
                {label: 'Device Overage', key: 'device_overage'},
            ],
            message: null,
            summaryComplete: false
        };

        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleSortOrFilterChange = this.handleSortOrFilterChange.bind(this);
    }

    componentWillMount() {
        this.fetchData('systems');
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.startDate !== this.props.startDate ||
            nextProps.endDate !== this.props.endDate ||
            nextProps.offset !== this.props.offset
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                summaryList: [],
                summaryTableHeaders: [],
                summaryComplete: false,
            });
        }
    }

    componentDidUpdate(prevProps) {
        if (
            prevProps.startDate !== this.props.startDate ||
            prevProps.endDate !== this.props.endDate ||
            prevProps.offset !== this.props.offset
        ) {
            this.fetchData('systems');
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get Systems data for a specific date range
     * @param type
     */
    fetchData(type) {
        // Assemble the URI
        let resource;
        const params = {
            start_date: this.props.startDate,
            end_date: this.props.endDate,
            offset: this.props.offset,
        };

        if (type === 'systems') {
            resource = reportsResources.SystemsReportGetAll.GET_LIST();
        } else if (type === 'usage') {
            resource = reportsResources.SystemsReportUsage.GET_LIST(params);
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data && result.data.payload) {
                this.processData(type, result.data.payload);
            }
        }).catch((error) => {
            let message = 'Sorry, there was an error retrieving Systems Reports.';
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
                console.log(error.request);
                message = 'Sorry, there was no response retrieving Systems Reports.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                message: message,
                summaryList: [],
                summaryTableData: [],
                summaryComplete: false
            }, () => {
                if (typeof this.props.onError === 'function') {
                    this.props.onError(message);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Store results
     *
     * @param type
     * @param data
     */
    processData(type, data) {
        if (data) {
            if (type === 'systems') {
                let summaryList = [];
                data.forEach(system => {
                    let locationLimit = system.location_limit;
                    let deviceLimit = system.device_limit;
                    const systemRow = {
                        system_name: (system.name) ? system.name : '----',
                        location_limit: (locationLimit) ? locationLimit : 0,
                        location_used: 0,
                        location_overage: 0,
                        device_limit: (deviceLimit) ? deviceLimit : 0,
                        device_used: 0,
                        device_overage: 0,
                    };
                    systemRow.display_name = (system.display_name) ? system.display_name : systemRow.system_name;
                    summaryList.push(systemRow);
                });
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList
                }, () => {
                    this.fetchData('usage');
                });
            }

            if (type === 'usage') {
                let summaryList = this.state.summaryList;
                data.aggregations.systems.buckets.forEach(system => {
                    const existingRow = summaryList.find(r => r.system_name === system.key);
                    if (existingRow) {
                        existingRow.location_used = (system.location_usage.value) ? system.location_usage.value : 0;
                        existingRow.device_used = (system.device_usage.value) ? system.device_usage.value : 0;
                        existingRow.location_overage = ((existingRow.location_used - existingRow.location_limit) > 0) ? existingRow.location_used - existingRow.location_limit : 0;
                        existingRow.device_overage = ((existingRow.device_used - existingRow.device_limit) > 0) ? existingRow.device_used - existingRow.device_limit : 0;
                    }
                });
                // Finalize table data
                this.setState({
                    summaryList: summaryList,
                    summaryTableData: summaryList,
                    summaryComplete: true
                }, () => {
                    this.props.onSummaryComplete(true);
                });
            }
        }
    }

    /**
     * Update selected table data based on sort
     *
     * @param event
     * @param table
     */
    handleSortOrFilterChange(event, table) {
        const tableData = getTableState(table);
        if (tableData && tableData.length > 0) {
            this.setState({
                summaryTableData: tableData
            });
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            endDate,
            startDate,
            system,
        } = this.props;

        const {
            summaryList,
            summaryTableData,
            summaryTableHeaders,
            summaryComplete,
        } = this.state;

        const dateString = convertStartEndDatesForFile(startDate, endDate);
        const defaultSort = setDefaultTableSort({field: 'display_name', order: 'ASC'});

        // ReactTable headers and columns for Table

        const tableColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'System',
                        accessor: 'display_name',
                    },
                    {
                        Header: 'Locations Limit',
                        accessor: 'location_limit',
                    },
                    {
                        Header: 'Locations Used',
                        accessor: 'location_used',
                    },
                    {
                        Header: 'Location Overage',
                        accessor: 'location_overage',
                    },
                    {
                        Header: 'Devices Limit',
                        accessor: 'device_limit',
                    },
                    {
                        Header: 'Devices Used',
                        accessor: 'device_used',
                    },
                    {
                        Header: 'Device Overage',
                        accessor: 'device_overage',
                    }
                ]
            }
        ];

        return (
            <div>
                {summaryComplete &&
                <div className="reports-table-container">

                    {/* Table Header: Systems */}
                    <div className="reports-table-title table-table-header">
                        <div className="btn-report-container report-header-button-container">
                            {/* Export Systems */}
                            <FlatButton
                                primary
                                type="button"
                                label=""
                                style={reportStyles.headerButton}
                            >
                                <ExportIcon style={reportStyles.headerButtonIcon}/>
                                <CSVLink
                                    data={summaryTableData}
                                    headers={summaryTableHeaders}
                                    filename={'Systems-' + system + '-' + dateString + '.csv'}
                                    onClick={(e) => this.handleExportClick(e)}
                                    target="_blank"
                                    style={reportStyles.headerButtonLink}
                                >
                                    Export Systems Report
                                </CSVLink>
                            </FlatButton>
                        </div>
                        <h3 className="table-table-header-title">
                            <TableIcon style={reportStyles.summaryIcon}/>
                            <span>Systems Report</span>
                        </h3>
                    </div>

                    {/* Table: Systems */}
                    <ReactTable
                        key="table-systems"
                        data={summaryList}
                        columns={tableColumns}
                        defaultPageSize={10}
                        showPagination={true}
                        defaultSorted={defaultSort}
                        filterable
                        defaultFilterMethod={filterCaseInsensitive}
                        onSortedChange={(e) => this.handleSortOrFilterChange(e, this.systemsReactTable)}
                        onFilteredChange={(e) => this.handleSortOrFilterChange(e, this.systemsReactTable)}
                        ref={(r) => this.systemsReactTable = r}
                        resizable={false}
                        minRows={1}
                        className="-striped overflow no-print"
                    />

                    <ReactTable
                        key="table-systems-print"
                        data={summaryTableData}
                        columns={tableColumns}
                        defaultPageSize={10000}
                        showPagination={false}
                        minRows={1}
                        className="print-only"
                    />
                </div>
                }
            </div>
        )
    }
}

SystemsReport.propTypes = {
    addField: PropTypes.bool,
    endDate: PropTypes.string,
    offset: PropTypes.string.isRequired,
    onError: PropTypes.func,
    onSummaryComplete: PropTypes.func,
    startDate: PropTypes.string,
    system: PropTypes.string,
};

SystemsReport.defaultProps = {
    addField: true,
    endDate: null,
    offset: null,
    startDate: null,
};

const enhance = compose(
    connect()
);

export default enhance(SystemsReport);



// WEBPACK FOOTER //
// ./src/Fuse/components/SystemsReport.js